import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FILTER_OPERATIONS, IN_PROGRESS, NOT_OPENED } from '../../constants';
import { buildColumns, buildOrderData } from '../../helpers/tableUtil';
import { FILTERS, SortDirection } from '../../models/global';
import { State } from '../../models/state';
import { getOrganizationSurveys } from '../../store/actions/organizationSurvey';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

const tableMap = new Map<string, string>([
  ['Survey series name', 'surveySeries.name'],
  ['Survey', ''],
  ['Survey creation date', 'createdAt'],
  ['Organization', 'organization.name'],
  ['Country', 'country.name'],
  ['Region', 'country.region'],
  ['Progress', 'survey.progress'],
]);
const { columns, rowsUnsortable } = buildColumns(tableMap);

function Submissions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const { organizationSurveys, organizationSurveyCount, dropdownValues } = useSelector(
    (state: State) => state.organizationSurvey,
  );
  const [filterByField, setFilterByField] = useState('');
  const [filterByValue, setFilterByValue] = useState('');
  const [filterOperation, setFilterOperation] = useState('');
  const [filters, setFilters] = useState<FILTERS[]>([
    {
      field: 'Progress',
      values: [NOT_OPENED],
      operation: FILTER_OPERATIONS[1],
    },
  ]);
  const [pageLoading, setPageLoading] = useState(false);

  const [orderBy, setOrderBy] = useState('Survey creation date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    const runOnce = () => {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    };
    if (location?.state?.message) {
      runOnce();
    }
  }, [location?.state?.message]);

  useEffect(() => {
    const orderData = buildOrderData({
      orderBy,
      sortDirection,
      tableMap,
    });

    (() => setPageLoading(true))();
    dispatch(
      getOrganizationSurveys(
        {
          limit: rowsPerPage,
          page: page + 1,
          orderData,
          filters,
        },
        () => setPageLoading(false),
      ),
    );
  }, [dispatch, filters, orderBy, page, sortDirection, rowsPerPage]);

  const addFilter = () => {
    if (!filterByValue || !filterByField || !filterOperation) {
      return;
    }
    const filter = filters.find(obj => obj.field === filterByField);
    if (filter) {
      filter.values.push(filterByValue);
    } else {
      filters.push({
        field: filterByField,
        values: [filterByValue],
        operation: filterOperation,
      });
    }
    setFilterByValue('');
    setFilterOperation('');
    setFilters([...filters]);
  };

  const deleteFilter = (filter: FILTERS, value: string) => {
    const foundFilter = filters.find(obj => obj.field === filter.field);
    foundFilter.values = foundFilter.values.filter(el => el !== value);
    if (!foundFilter.values.length) {
      setFilters(filters.filter(el => el.field !== filter.field));
    } else {
      setFilters([...filters]);
    }
  };

  const organizationSurveyData = organizationSurveys.map(organizationSurvey => {
    if (organizationSurvey.organization) {
      organizationSurvey.country = organizationSurvey.organization.country?.name;
      organizationSurvey.region = organizationSurvey.organization.country?.region;
    }
    if (organizationSurvey.progress === IN_PROGRESS && organizationSurvey.progressPercentage) {
      organizationSurvey.progress = `${organizationSurvey.progress} ${organizationSurvey.progressPercentage}%`;
    }
    return organizationSurvey;
  });

  const filteringOptions = Object.keys(dropdownValues).map(item => item.charAt(0).toUpperCase() + item.slice(1));

  const filterValueLabelStyle = {
    marginRight: 1,
    paddingLeft: 1,
    paddingRight: 1,
    backgroundColor: '#3d82d0',
    color: '#fff',
  };

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <>
      <Box className='list'>
        <Box className='list-header' sx={{ mb: 2 }}>
          <Typography variant='h5'>Survey administration</Typography>
          <Box display='flex' gap={2}>
            <Dropdown
              value={filterByField}
              label='Filter by field'
              options={filteringOptions}
              onChange={(_: SyntheticEvent, value: string) => {
                setFilterByField(value);
                setFilterByValue('');
              }}
            />
            <Dropdown
              value={filterOperation}
              label='Filter operation'
              options={FILTER_OPERATIONS.filter(operation => {
                const filter = filters.find(filter => filter.field === filterByField);
                if (filter) {
                  return filter.operation === operation;
                }
                return true;
              })}
              onChange={(_: SyntheticEvent, value: string) => {
                setFilterOperation(value);
              }}
              disableClearable
            />
            <Dropdown
              value={filterByValue}
              label='Filter by value'
              options={
                (dropdownValues[filterByField?.toLowerCase()] || []).filter(
                  item => !filters.some(filter => filter.field === filterByField && filter.values.includes(item)),
                ) || []
              }
              onChange={(_: SyntheticEvent, value: string) => setFilterByValue(value)}
              disabled={!filterByField}
            />
            <Button disabled={!filterByValue || !filterByField || !filterOperation} text='Add' onClick={addFilter} />
          </Box>
        </Box>
        {filters.map(filter => (
          <Box key={filter.field} display='flex' gap={1} alignItems='center' mb={2}>
            <Typography>{filter.field}</Typography>
            <Typography>{filter.operation.toLowerCase()}</Typography>
            <Box>
              {filter.values.map((value: string) => (
                <Chip
                  key={value}
                  label={value}
                  sx={filterValueLabelStyle}
                  size='small'
                  deleteIcon={<ClearIcon style={{ color: '#fff' }} />}
                  onDelete={() => deleteFilter(filter, value)}
                />
              ))}
            </Box>
          </Box>
        ))}
        <Table
          columns={columns}
          rowsUnsortable={rowsUnsortable}
          rows={organizationSurveyData}
          count={organizationSurveyCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          rowsPerPageOptions={[10, 20, 50, 250]}
          onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowClickHandler={(id: string) => {
            const organizationSurvey = organizationSurveyData.find(el => el.id === id);
            if (organizationSurvey.progress !== NOT_OPENED) {
              navigate(
                `/survey-series/${organizationSurvey.surveySeriesId}/survey/${organizationSurvey.surveyId}/${organizationSurvey.organizationId}`,
              );
            }
          }}
          actionIcons={[
            {
              title: 'Copy survey link',
              icon: <ContentCopyIcon key='copy' />,
              onClick: id => {
                const organizationSurvey = organizationSurveyData.find(el => el.id === id);
                navigator.clipboard.writeText(
                  `${window.location.origin}/survey-series/${organizationSurvey.surveySeriesId}/survey/${organizationSurvey.surveyId}`,
                );
                setToastType('success');
                setToast('Survey link copied');
              },
            },
          ]}
          orderBy={orderBy}
          handleOrder={setOrderBy}
          sortDirection={sortDirection}
          handleSortDirection={setSortDirection}
        />
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default Submissions;
